import { EXPORT_DPI } from "./gangsheetMath";
import type { TrimResult } from "../types/gangsheet";

export function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function trimTransparentEdges(file: File): Promise<TrimResult> {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (!ctx) {
    throw new Error("Could not read image.");
  }

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 5) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }

  if (right === -1 || bottom === -1) {
    return {
      dataUrl,
      width: img.width,
      height: img.height,
    };
  }

  const trimWidth = right - left + 1;
  const trimHeight = bottom - top + 1;

  const out = document.createElement("canvas");
  const outCtx = out.getContext("2d");

  if (!outCtx) {
    throw new Error("Could not process trimmed image.");
  }

  out.width = trimWidth;
  out.height = trimHeight;
  outCtx.drawImage(
    canvas,
    left,
    top,
    trimWidth,
    trimHeight,
    0,
    0,
    trimWidth,
    trimHeight
  );

  return {
    dataUrl: out.toDataURL("image/png"),
    width: trimWidth,
    height: trimHeight,
  };
}

export function buildDefaultSizeInches(widthPx: number, heightPx: number) {
  const nativeW = widthPx / EXPORT_DPI;
  const nativeH = heightPx / EXPORT_DPI;

  let widthIn = nativeW;
  let heightIn = nativeH;

  const maxInitialWidth = 11;
  const maxInitialHeight = 11;

  const scale = Math.min(
    maxInitialWidth / Math.max(widthIn, 0.001),
    maxInitialHeight / Math.max(heightIn, 0.001),
    1
  );

  widthIn *= scale;
  heightIn *= scale;

  widthIn = Math.max(widthIn, 1.5);
  heightIn = Math.max(heightIn, 1.5 * (heightPx / Math.max(widthPx, 1)));

  return { widthIn, heightIn };
}